import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import DanaOrderUpload from "@dana/Components/DanaOrderUpload/DanaOrderUpload";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import addToWishList, { AddToWishListParameter } from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import loadWishListIfNeeded from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListIfNeeded";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import OrderUpload, { OrderUploadStyles } from "@insite/content-library/Components/OrderUpload";
import OrderUploadErrorsModal, {
    OrderUploadErrorsModalStyles,
} from "@insite/content-library/Components/OrderUploadErrorsModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface UploadItemsToListStyles {
    uploadItemsGridItem?: GridItemProps;
    uploadItemsButton?: ButtonPresentationProps;
    uploadItemsLink?: LinkPresentationProps;
    uploadItemsModal?: ModalPresentationProps;
    orderUploadStyles?: OrderUploadStyles;
    orderUploadErrorsModalStyles?: OrderUploadErrorsModalStyles;
}

export const uploadItemsToListStyles: UploadItemsToListStyles = {
    uploadItemsGridItem: {
        width: [12, 12, 12, 2, 2],
        css: css`
            align-items: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        padding-top: 30px;
                    `,
                    css`
                        padding-top: 30px;
                    `,
                ])}
        `,
    },
    uploadItemsLink: {
        css: css`
            margin: 15px 0 0;
        `,
    },
    uploadItemsModal: {
        size: 1110,
        cssOverrides: {
            modalTitle: css`
                padding: 10px 30px;
            `,
            modalContent: css`
                padding: 20px 30px;
                [class*="OrderedListStyle"] {
                    margin: 0;
                }
            `,
        },
    },
};


const mapStateToProps = (state: ApplicationState) => ({
    isEmea: useIsEmea(state),
    danaGeneralSettings: getDanaGeneralSettings(state)
});

const mapDispatchToProps = {
    loadWishListIfNeeded,
    addToWishList: makeHandlerChainAwaitable<AddToWishListParameter, WishListModel>(addToWishList),
};

interface UploadItemsToListProps {
    id?: string;
    wishList: WishListModel;
    displayTypeButton?: boolean;
    handleClose?: () => void;
}
type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & UploadItemsToListProps;
const styles = uploadItemsToListStyles;
const UploadItemsToList = ({
    wishList,
    isEmea,
    addToWishList,
    loadWishListIfNeeded,
    displayTypeButton = false,
    danaGeneralSettings,
    handleClose
}: Props) => {
    const [uploadItemsModalIsOpen, setUploadItemsModalIsOpen] = useState(false);
    const [downloadTemplate, setTemplate] = useState("");

    useEffect(() => {
        if (isEmea) {
            setTemplate("/userfiles/ListUploadEmeaTemplate.xlsx");
        } else {
            setTemplate("/userfiles/ListUploadTemplate.xlsx");
        }
    }, [isEmea]);


    const uploadItemsClickHandler = () => {
        setUploadItemsModalIsOpen(true);
    };

    const uploadItemsModalCloseHandler = () => {
      
        if (handleClose) {
            handleClose()
        }
        setUploadItemsModalIsOpen(false);

    };

    const uploadProductsHandler = async (products: any[]) => {

        const productInfos = products.map(o => ({
            productId: o.id,
            qtyOrdered: o.qtyOrdered,
            unitOfMeasure: o.selectedUnitOfMeasure ?? "",
            properties: {
                requestedShipDate: o.properties.requestedShipDate,
            } as any,
        }));

        await addToWishList({ productInfos, selectedWishList: wishList });
        
        // setUploadItemsModalIsOpen(false);
        // loadWishListIfNeeded({ wishListId: wishList.id });
    };

    return (
        <>
            {displayTypeButton && (
                <Button
                    {...styles.uploadItemsButton}
                    onClick={uploadItemsClickHandler}
                    data-test-selector="myListDetails_uploadItems"  >
                    {translate("Upload Items")}
                </Button>
            )}
            {!displayTypeButton && (
                <GridItem {...styles.uploadItemsGridItem}>
                    <Link
                        {...styles.uploadItemsLink}
                        onClick={uploadItemsClickHandler}
                        data-test-selector="myListDetails_uploadItems"
                    >
                        {translate("Upload Items")}
                    </Link>
                </GridItem>
            )}
            {danaGeneralSettings?.enableCopyPasteFeatureForMyList ? (
                <DanaOrderUpload
                    onUploadProducts={uploadProductsHandler}
                    templateUrl={downloadTemplate}
                    isOpen={uploadItemsModalIsOpen}
                    handleClose={uploadItemsModalCloseHandler}
                    wishListId={wishList.id}
                    wishListName={wishList.name}
                />
            ) :
                (
                    <Modal
                        {...styles.uploadItemsModal}
                        headline={translate("Upload Items To List")}
                        isOpen={uploadItemsModalIsOpen}
                        handleClose={uploadItemsModalCloseHandler}
                        data-test-selector="myListDetails_uploadItemsToListModal"
                    >
                        <GridContainer>
                            <GridItem width={[12, 12, 6, 6, 6]}>
                                <OrderUpload
                                    descriptionText={siteMessage("Lists_UploadLinkTooltip")}
                                    checkInventory={false}
                                    templateUrl={downloadTemplate}
                                    onUploadProducts={uploadProductsHandler}
                                    extendedStyles={styles.orderUploadStyles}
                                />
                                <OrderUploadErrorsModal
                                    descriptionText={siteMessage("ListUpload_UploadError")}
                                    uploadErrorText={siteMessage("ListUpload_UploadError")}
                                    rowsLimitExceededText={siteMessage("ListUpload_RowsLimitExceeded")}
                                    errorReasons={{
                                        [UploadError.ConfigurableProduct]: siteMessage(
                                            "ListUpload_CannotOrderConfigurable",
                                        ),
                                        [UploadError.StyledProduct]: siteMessage("ListUpload_CannotOrderStyled"),
                                        [UploadError.InvalidUnit]: translate("Invalid U/M"),
                                        [UploadError.NotFound]: siteMessage("Product_NotFound"),
                                        [UploadError.OutOfStock]: translate(
                                            "This product is obsolete, not allowed to be added to the cart.",
                                        ),
                                    }}
                                    extendedStyles={styles.orderUploadErrorsModalStyles}
                                />
                            </GridItem>
                            <GridItem width={[12, 12, 6, 6, 6]}>
                                {siteMessage("UploadItemsToList_Instructions")}
                            </GridItem>
                        </GridContainer>
                    </Modal>
                )}


        </>

    );
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadItemsToList);
